import { create } from 'zustand'
import type { ViewerHandle } from '@/three/viewer'

// One store between the chrome and the 3D layer.
//
// The viewer handle is held here rather than passed down through props: every
// tool panel drives the same viewer, and threading one object through six
// components buys nothing. Actions are thin — they set UI state and forward to
// the handle — so the store never becomes a second source of truth for
// something the viewer already owns.

export type Tool = 'residences' | 'time' | 'interiors' | 'info' | null

interface AppState {
  viewer: ViewerHandle | null
  progress: number
  loaded: boolean

  tool: Tool
  walking: boolean
  markers: boolean
  /** Slider position, 0-1 across 05:00-23:00. */
  timeOfDay: number
  liveTime: boolean
  /** Index into the apartment list, or null for all. */
  residence: number | null
  theme: 'light' | 'dark'
  registerOpen: boolean

  setTheme: (theme: 'light' | 'dark') => void
  setRegisterOpen: (open: boolean) => void
  setViewer: (viewer: ViewerHandle | null) => void
  setProgress: (progress: number) => void
  setLoaded: (loaded: boolean) => void

  toggleTool: (tool: Tool) => void
  closeTool: () => void
  setMarkers: (on: boolean) => void
  setTimeOfDay: (t: number) => void
  setLiveTime: (on: boolean) => void
  setResidence: (index: number | null) => void
  enterWalk: () => void
  exitWalk: () => void
  /** The walk can end on its own (Esc, dropped pointer lock). */
  walkEnded: () => void
}

export const useAppStore = create<AppState>((set, get) => ({
  viewer: null,
  progress: 0,
  loaded: false,

  tool: null,
  walking: false,
  markers: true,
  timeOfDay: 0.5,
  liveTime: true,
  residence: null,
  theme: 'dark',
  registerOpen: false,

  setTheme: (theme) => {
    // The token flip lives on the root element, same as Tower.
    document.documentElement.dataset.theme = theme
    set({ theme })
  },
  setRegisterOpen: (registerOpen) => set({ registerOpen }),
  setViewer: (viewer) => set({ viewer }),
  setProgress: (progress) => set({ progress }),
  setLoaded: (loaded) => set({ loaded }),

  toggleTool: (tool) => set((s) => ({ tool: s.tool === tool ? null : tool })),
  closeTool: () => set({ tool: null }),

  setMarkers: (on) => {
    get().viewer?.setMarkers(on)
    set({ markers: on })
  },
  setTimeOfDay: (t) => {
    get().viewer?.setTimeOfDay(t)
    set({ timeOfDay: t, liveTime: false })
  },
  setLiveTime: (on) => {
    get().viewer?.setLiveTime(on)
    set({ liveTime: on })
  },
  setResidence: (index) => {
    get().viewer?.setResidenceFilter(index)
    set({ residence: index })
  },
  enterWalk: () => {
    if (get().viewer?.enterWalk()) set({ walking: true, tool: null })
  },
  exitWalk: () => {
    get().viewer?.exitWalk()
    set({ walking: false })
  },
  walkEnded: () => set({ walking: false }),
}))
